import React, { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Box,
  IconButton,
  Typography,
  Tooltip,
} from "@mui/material";
import { ZoomIn, ZoomOut, ZoomOutMap, Undo } from "@mui/icons-material";
import { DEFAULT_SYMBOL, getSymbolDetails } from "../../utils/stitchSymbols";
import "./ChartGrid.css";

const ChartGrid = ({
  chart,
  onChartChange,
  readOnly = false,
  showRowNumbers = true,
  showColumnNumbers = true,
  selectedSymbol = null,
  inTheRound = false,
  onSelectedStitchClick = null,
}) => {
  const [zoomLevel, setZoomLevel] = useState(1);
  const [isDragging, setIsDragging] = useState(false);
  const [history, setHistory] = useState([]);
  const [canUndo, setCanUndo] = useState(false);

  // Zoom levels: 0.5x, 0.75x, 1x, 1.25x, 1.5x
  const zoomLevels = [0.5, 0.75, 1, 1.25, 1.5];
  const currentZoomIndex = zoomLevels.indexOf(zoomLevel);

  const handleZoomIn = () => {
    if (currentZoomIndex < zoomLevels.length - 1) {
      setZoomLevel(zoomLevels[currentZoomIndex + 1]);
    }
  };

  const handleZoomOut = () => {
    if (currentZoomIndex > 0) {
      setZoomLevel(zoomLevels[currentZoomIndex - 1]);
    }
  };

  const handleZoomFit = () => {
    // Calculate zoom to fit grid in viewport
    const containerWidth = 800; // Approximate container width
    const cellSize = 48; // Base cell size
    const gridWidth = chart.width * cellSize;
    const gridHeight = chart.height * cellSize;

    const scaleX = containerWidth / gridWidth;
    const scaleY = containerWidth / gridHeight;
    const scale = Math.min(scaleX, scaleY, 1); // Don't zoom in beyond 100%

    // Find closest zoom level
    const closestZoom = zoomLevels.reduce((prev, curr) =>
      Math.abs(curr - scale) < Math.abs(prev - scale) ? curr : prev,
    );

    setZoomLevel(closestZoom);
  };

  // Save current state to history before making changes
  const saveToHistory = () => {
    const currentState = { ...chart, rows: { ...chart.rows } };
    setHistory((prev) => {
      const newHistory = [...prev, currentState];
      // Cap history at 50 states to prevent memory issues
      return newHistory.length > 50 ? newHistory.slice(-50) : newHistory;
    });
    setCanUndo(true);
  };

  // Undo the last change
  const handleUndo = () => {
    if (history.length > 0) {
      const previousState = history[history.length - 1];
      setHistory((prev) => prev.slice(0, -1));
      setCanUndo(history.length > 1);
      onChartChange(previousState);
    }
  };

  // Handle cell click for brush mode
  const handleCellClick = (rowIndex, colIndex) => {
    if (readOnly || !selectedSymbol) return;

    // Save current state before making changes (only for single clicks)
    saveToHistory();

    const newChart = { ...chart };
    const rowKey = `row${rowIndex}`;

    if (!newChart.rows[rowKey]) {
      newChart.rows[rowKey] = Array(chart.width).fill(DEFAULT_SYMBOL);
    }

    newChart.rows[rowKey] = [...newChart.rows[rowKey]];
    newChart.rows[rowKey][colIndex] = selectedSymbol;

    onChartChange(newChart);
  };

  // Handle pointer down for drag start
  const handleCellPointerDown = (rowIndex, colIndex) => {
    if (readOnly || !selectedSymbol) return;

    // Save current state before starting drag operation
    saveToHistory();

    setIsDragging(true);

    // Apply symbol to the starting cell
    const newChart = { ...chart };
    const rowKey = `row${rowIndex}`;

    if (!newChart.rows[rowKey]) {
      newChart.rows[rowKey] = Array(chart.width).fill(DEFAULT_SYMBOL);
    }

    newChart.rows[rowKey] = [...newChart.rows[rowKey]];
    newChart.rows[rowKey][colIndex] = selectedSymbol;

    onChartChange(newChart);
  };

  // Handle pointer enter for drag continuation - more reliable than mouseenter for fast movements
  const handleCellPointerEnter = (rowIndex, colIndex) => {
    if (readOnly || !selectedSymbol || !isDragging) return;

    const newChart = { ...chart };
    const rowKey = `row${rowIndex}`;

    if (!newChart.rows[rowKey]) {
      newChart.rows[rowKey] = Array(chart.width).fill(DEFAULT_SYMBOL);
    }

    newChart.rows[rowKey] = [...newChart.rows[rowKey]];
    newChart.rows[rowKey][colIndex] = selectedSymbol;

    onChartChange(newChart);
  };

  // Handle pointer up for drag end
  const handleCellPointerUp = () => {
    setIsDragging(false);
  };

  // Handle pointer leave to end drag when leaving the grid
  const handleGridPointerLeave = () => {
    setIsDragging(false);
  };

  // Get cell value
  const getCellValue = (rowIndex, colIndex) => {
    const rowKey = `row${rowIndex}`;
    return chart.rows[rowKey]?.[colIndex] || DEFAULT_SYMBOL;
  };

  // Generate column headers
  const columnHeaders = [];
  if (showColumnNumbers) {
    for (let i = 0; i < chart.width; i++) {
      columnHeaders.push(i);
    }
  }

  // Get selected symbol details
  const selectedSymbolDetails = selectedSymbol ? getSymbolDetails(selectedSymbol) : null;

  return (
    <Box className="chart-grid-wrapper">
      {/* Controls Bar - Fixed outside scrollable area */}
      <Box className="chart-grid-controls">
        {/* Left Block: Grid Size + Selected Chip */}
        <Box className="chart-grid-left-block">
          <Typography variant="body2" color="text.secondary" className="chart-grid-size">
            Grid Size: {chart.width} × {chart.height}
          </Typography>
          {selectedSymbol && selectedSymbolDetails && (
            <Box
              className={`chart-grid-selected-chip ${!onSelectedStitchClick ? "chart-grid-selected-chip-default-cursor" : ""}`}
              onClick={onSelectedStitchClick || undefined}
            >
              <Box className="chart-grid-selected-chip-symbol">
                <Typography
                  variant="body2"
                  className="chart-grid-selected-chip-glyph"
                >
                  {selectedSymbol}
                </Typography>
              </Box>
              <Typography
                variant="caption"
                className="chart-grid-selected-chip-label"
              >
                {selectedSymbolDetails.abbreviation}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Right Block: Actions Group */}
        <Box className="chart-grid-zoom-controls">
          <Tooltip title="Undo Last Change">
            <span>
              <IconButton size="small" onClick={handleUndo} disabled={!canUndo}>
                <Undo />
              </IconButton>
            </span>
          </Tooltip>

          <Tooltip title="Zoom Out">
            <span>
              <IconButton
                size="small"
                onClick={handleZoomOut}
                disabled={currentZoomIndex === 0}
              >
                <ZoomOut />
              </IconButton>
            </span>
          </Tooltip>

          <Tooltip title="Zoom In">
            <span>
              <IconButton
                size="small"
                onClick={handleZoomIn}
                disabled={currentZoomIndex === zoomLevels.length - 1}
              >
                <ZoomIn />
              </IconButton>
            </span>
          </Tooltip>

          <Tooltip title="Zoom to Fit">
            <IconButton size="small" onClick={handleZoomFit}>
              <ZoomOutMap />
            </IconButton>
          </Tooltip>

          <Typography
            variant="body2"
            color="text.secondary"
            className="chart-grid-zoom-percentage"
          >
            {Math.round(zoomLevel * 100)}%
          </Typography>
        </Box>
      </Box>

      {/* Scrollable Chart Container */}
      <Box className="chart-grid-container">
        <div
        style={{
          display: "inline-block",
          width: `${(chart.width * 48 + (showRowNumbers ? 44 * 2 : 0) + Math.max(1, Math.ceil(1.5 / zoomLevel))) * zoomLevel}px`,
          height: `${(chart.height * 48 + (showColumnNumbers ? 44 * 2 : 0) + Math.max(1, Math.ceil(1.5 / zoomLevel))) * zoomLevel + 10}px`,
          padding: `${Math.max(0.5, Math.ceil(0.75 / zoomLevel))}px`,
          margin: "0 auto",
        }}
      >
        <Box
          className="chart-grid-scaled-container"
          style={{
            transform: `scale(${zoomLevel})`,
            transformOrigin: "top left",
            width: `${chart.width * 48 + (showRowNumbers ? 44 * 2 : 0)}px`,
            height: `${chart.height * 48 + (showColumnNumbers ? 44 * 2 : 0)}px`,
          }}
        >
          <Table
            id="chart-grid"
            className="chart-grid-table"
            onPointerLeave={handleGridPointerLeave}
            style={{
              minWidth: chart.width * 48 + (showRowNumbers ? 44 * 2 : 0),
              maxWidth: chart.width * 48 + (showRowNumbers ? 44 * 2 : 0),
              "--border-width": `${Math.max(0.5, Math.ceil(0.75 / zoomLevel))}px`,
              userSelect: "none",
              WebkitUserSelect: "none",
              MozUserSelect: "none",
              msUserSelect: "none",
            }}
          >
            {/* Column headers - top row showing column numbers */}
            {/* Render columns in reverse order: Column 1 at right, highest column at left */}
            {showColumnNumbers && (
              <TableHead>
                <TableRow>
                  {/* Empty cell for row number space on left */}
                  {showRowNumbers && (
                    <TableCell className="chart-grid-row-header"></TableCell>
                  )}
                  {columnHeaders.map((colNum) => {
                    // Reverse column display: colNum 0 (leftmost) shows as chart.width (rightmost)
                    // colNum (width-1) (rightmost) shows as 1
                    const displayColNumber = chart.width - colNum;
                    return (
                      <TableCell
                        key={colNum}
                        className="chart-grid-column-header"
                      >
                        {displayColNumber}
                      </TableCell>
                    );
                  })}
                  {/* Empty cell for row number space on right */}
                  {showRowNumbers && (
                    <TableCell className="chart-grid-row-header"></TableCell>
                  )}
                </TableRow>
              </TableHead>
            )}

            {/* Data rows */}
            {/* Render rows in reverse order: Row 1 at bottom, highest row at top */}
            <TableBody>
              {Array.from({ length: chart.height }, (_, displayIndex) => {
                // Reverse the display order: displayIndex 0 shows the last row (highest number)
                // displayIndex (height-1) shows row 0 (Row 1)
                const rowIndex = chart.height - 1 - displayIndex;
                const displayRowNumber = rowIndex + 1; // 1-based row number for display
                // In the round: all rows are worked right to left, so numbers always on right
                // Flat knitting: alternate based on RS/WS
                const isRightSide = inTheRound ? true : displayRowNumber % 2 === 1;

                return (
                  <TableRow key={rowIndex}>
                    {/* Row number on LEFT for WS rows (even), empty cell for RS rows (odd) */}
                    {showRowNumbers && (
                      <TableCell className="chart-grid-column-header">
                        {!isRightSide ? displayRowNumber : ""}
                      </TableCell>
                    )}

                    {/* Data cells */}
                    {/* Render columns in reverse order: Column 1 at right, highest column at left */}
                    {Array.from({ length: chart.width }, (_, displayColIndex) => {
                      // Reverse column display order: displayColIndex 0 shows the rightmost column
                      // displayColIndex (width-1) shows the leftmost column
                      const colIndex = chart.width - 1 - displayColIndex;
                      const cellValue = getCellValue(rowIndex, colIndex);

                      return (
                        <TableCell
                          key={colIndex}
                          onClick={() => handleCellClick(rowIndex, colIndex)}
                          onPointerDown={() =>
                            handleCellPointerDown(rowIndex, colIndex)
                          }
                          onPointerEnter={() =>
                            handleCellPointerEnter(rowIndex, colIndex)
                          }
                          onPointerUp={handleCellPointerUp}
                          className={`chart-grid-cell ${isDragging ? "chart-grid-cell-dragging" : ""} ${!selectedSymbol ? "chart-grid-cell-not-selectable" : ""}`}
                        >
                          <div className="chart-grid-cell-content">
                            {cellValue}
                          </div>
                        </TableCell>
                      );
                    })}

                    {/* Row number on RIGHT for RS rows (odd), empty cell for WS rows (even) */}
                    {showRowNumbers && (
                      <TableCell className="chart-grid-column-header">
                        {isRightSide ? displayRowNumber : ""}
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>

            {/* Column footer - bottom row showing column numbers */}
            {showColumnNumbers && (
              <TableHead>
                <TableRow>
                  {/* Empty cell for row number space on left */}
                  {showRowNumbers && (
                    <TableCell className="chart-grid-row-header"></TableCell>
                  )}
                  {columnHeaders.map((colNum) => {
                    // Reverse column display: colNum 0 (leftmost) shows as chart.width (rightmost)
                    // colNum (width-1) (rightmost) shows as 1
                    const displayColNumber = chart.width - colNum;
                    return (
                      <TableCell
                        key={colNum}
                        className="chart-grid-column-header"
                      >
                        {displayColNumber}
                      </TableCell>
                    );
                  })}
                  {/* Empty cell for row number space on right */}
                  {showRowNumbers && (
                    <TableCell className="chart-grid-row-header"></TableCell>
                  )}
                </TableRow>
              </TableHead>
            )}
          </Table>
        </Box>
      </div>
      </Box>
    </Box>
  );
};

export default ChartGrid;
